import React, { useState } from 'react';
import { Modal, Button, Input, Select, message } from 'antd';
import { CloseOutlined, PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { MergedOrder } from '../../../../types/process/order';
import { orderApi } from '../../../../services/process/order';
import { OperationAction } from '../../../../types/process/operation-log';

const { TextArea } = Input;

export interface DecisionModalProps {
  visible: boolean;
  order: MergedOrder | null;
  onCancel: () => void;
  onSuccess: () => void;
  defaultActiveKey?: string;
  source?: 'decision' | 'delivery';
}

const DecisionModal: React.FC<DecisionModalProps> = ({
  visible,
  order,
  onCancel,
  onSuccess,
  defaultActiveKey = 'pass',
  source = 'decision',
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultActiveKey);
  const [purchaseType, setPurchaseType] = useState<'platform' | 'self'>('platform');
  const [loading, setLoading] = useState<boolean>(false);
  const [decisionMemo, setDecisionMemo] = useState<string>('');
  const [activationStatus, setActivationStatus] = useState<number | undefined>();
  const [lockStatus, setLockStatus] = useState<number | undefined>();
  // 自采部分
  const [pickupMethod, setPickupMethod] = useState<string | undefined>();
  const [requirementRemark, setRequirementRemark] = useState<string>('');

  // 预收部分
  const [advancePeriod, setAdvancePeriod] = useState<string>('');

  // 补充材料部分
  const defaultMaterials = [
    '客户营业执照副本及最近一次年检证明',
    '近三个月银行流水及财务报表',
    '法人身份证明及授权委托书',
    '历史交易记录及信用报告',
    '相关资质证书（如适用）',
  ];
  const [materials, setMaterials] = useState<{ id: string; text: string; isDefault: boolean }[]>(
    defaultMaterials.map((text, i) => ({ id: `default-${i}`, text, isDefault: true }))
  );
  const [newMaterial, setNewMaterial] = useState<string>('');

  const handleCancelClick = () => {
    setActiveTab(defaultActiveKey);
    setPurchaseType('platform');
    setActivationStatus(undefined);
    setLockStatus(undefined);
    setDecisionMemo('');
    setAdvancePeriod('');
    setPickupMethod(undefined);
    setRequirementRemark('');
    setNewMaterial('');
    onCancel();
  };

  const handleAddMaterial = () => {
    if (newMaterial.trim()) {
      setMaterials([...materials, { id: `custom-${Date.now()}`, text: newMaterial.trim(), isDefault: false }]);
      setNewMaterial('');
    }
  };

  const handleDeleteMaterial = (id: string) => {
    setMaterials(materials.filter((m) => m.id !== id));
  };

  const handleSubmit = async () => {
    if (!order) return;

    if (activeTab === 'pass') {
      try {
        setLoading(true);
        if (purchaseType === 'platform') {
          // 平台发货
          await orderApi.changeStatus(order.order.orderNo, OperationAction.SubmitDelivery, {
            deliveryType: purchaseType,
          });
          message.success('已成功去人人租平台发货');
        } else {
          // 自采发货
          if (!activationStatus || !lockStatus || !pickupMethod) {
            message.warning('请填写所有带 * 的标星需求');
            setLoading(false);
            return;
          }
          await orderApi.changeStatus(order.order.orderNo, OperationAction.SubmitDelivery, {
            deliveryType: purchaseType,
            activationStatus,
            lockStatus,
            pickupMethod,
            requirementRemark,
          });
          message.success('已成功下发采购需求');
        }
        onSuccess();
      } catch (error: any) {
        message.error(error.message || '发货/采购操作失败');
      } finally {
        setLoading(false);
      }
    } else if (activeTab === 'reject') {
      try {
        setLoading(true);
        await orderApi.cancelOrder(order.order.orderNo, {
          cancelType: 'CANCEL_ORDER',
          cancelCategory: '其他',
          cancelReason: '高风险或低利润，建议取消订单',
        });
        
        // 更新决策状态为取消
        await orderApi.changeStatus(order.order.orderNo, OperationAction.SubmitDecisionReject);
        
        message.success('建议取消订单操作成功');
        onSuccess();
      } catch (error: any) {
        message.error(error.message || '取消订单操作失败');
      } finally {
        setLoading(false);
      }
    } else if (activeTab === 'advance') {
      if (!advancePeriod) {
        message.warning('请输入预收期数');
        return;
      }
      message.info('预收功能正在开发中，暂不处理。参数: ' + advancePeriod);
    } else if (activeTab === 'supplement') {
      message.info('补充材料功能正在开发中，暂不处理。');
    }
  };

  if (!order) return null;

  const titlePrefix = '审核主管决策';

  const renderRadioCircle = (active: boolean) => (
    <div
      style={{
        marginTop: 2,
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: active ? '5px solid #2785f4' : '2px solid #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
      }}
    />
  );

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancelClick}
      width={720}
      footer={null}
      closable={false}
      bodyStyle={{ padding: 0 }}
      style={{ top: 40 }} // Move modal higher up the screen
      destroyOnClose
    >
      <div style={{ backgroundColor: '#fff', borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%', position: 'relative' }}>

        {/* Header */}
        <div style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 20, color: '#0a0a0a', lineHeight: '30px', fontFamily: 'sans-serif' }}>{titlePrefix}</span>
            <span style={{ color: '#6b7280', fontSize: 14, lineHeight: '21px', fontFamily: 'sans-serif' }}>订单号: {order.order.orderNo}</span>
          </div>
          <Button type="text" onClick={handleCancelClick} icon={<CloseOutlined style={{ color: '#6b7280', fontSize: 18 }} />} />
        </div>

        {/* Content Area */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <p style={{ color: '#0a0a0a', fontSize: 16, fontWeight: 500, lineHeight: '24px', margin: 0, fontFamily: 'sans-serif' }}>请选择：</p>

          {/* Cards Group */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* 1. 放单 */}
            <div
              style={{
                border: `1px solid ${activeTab === 'pass' ? '#2785f4' : '#e5e7eb'}`,
                borderRadius: 8,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                cursor: 'pointer',
              }}
              onClick={() => setActiveTab('pass')}
            >
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {renderRadioCircle(activeTab === 'pass')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, fontSize: 16, color: '#0a0a0a', lineHeight: '24px', fontFamily: 'sans-serif' }}>放单</span>
                    <span style={{ fontSize: 13, color: '#6b7280', lineHeight: '20px', fontFamily: 'sans-serif', marginTop: -2 }}>
                      审核通过，直接放单发货
                    </span>
                  </div>
                </div>

                {activeTab === 'pass' && (
                  <div style={{ borderTop: '1px solid #dbeafe', paddingTop: 16, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ border: '1px solid #dbeafe', borderRadius: 6, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#6b7280', fontSize: 14, fontFamily: 'sans-serif' }}>应发货品:</span>
                      <span style={{ color: '#2785f4', fontSize: 14, fontWeight: 500, fontFamily: 'sans-serif' }}>
                        {order.order.productTitle || '无明确数据'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <span style={{ color: '#0a0a0a', fontSize: 14, fontWeight: 500, fontFamily: 'sans-serif' }}>采购方式：</span>

                      {/* 平台采购 Option */}
                      <div
                        style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); setPurchaseType('platform'); }}
                      >
                        <div
                          style={{
                            marginTop: 2, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            border: purchaseType === 'platform' ? '4px solid #2785f4' : '1px solid #d1d5db'
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2 }}>
                          <span style={{ color: '#0a0a0a', fontSize: 14, fontWeight: 500, lineHeight: '22px', fontFamily: 'sans-serif' }}>平台采购</span>
                          <span style={{ color: '#6b7280', fontSize: 13, lineHeight: '20px', fontFamily: 'sans-serif' }}>使用平台库存设备，前往人人租平台完成发货</span>
                        </div>
                      </div>

                      {/* 自行采购 Option */}
                      <div
                        style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', paddingTop: 4 }}
                        onClick={(e) => { e.stopPropagation(); setPurchaseType('self'); }}
                      >
                        <div
                          style={{
                            marginTop: 2, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            border: purchaseType === 'self' ? '4px solid #2785f4' : '1px solid #d1d5db'
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2, width: '100%' }}>
                          <span style={{ color: '#0a0a0a', fontSize: 14, fontWeight: 500, lineHeight: '22px', fontFamily: 'sans-serif' }}>自行采购</span>
                          <span style={{ color: '#6b7280', fontSize: 13, lineHeight: '20px', fontFamily: 'sans-serif', marginBottom: purchaseType === 'self' ? 12 : 0 }}>下发采购需求给采购人员，自行采购设备</span>

                          {/* Self Purchase Form */}
                          {purchaseType === 'self' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ backgroundColor: '#eff6ff', border: '1px solid rgba(219,234,254,0.5)', borderRadius: 6, padding: 12, fontSize: 13, color: '#2785f4', fontWeight: 500, lineHeight: '20px' }}>
                                需填写采购需求（商品信息、激活情况、上锁情况）给采购人员，采购人员向供应商询价后反馈。
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a' }}>激活需求 <span style={{ color: '#ef4444' }}>*</span></div>
                                  <Select placeholder="请选择" value={activationStatus} onChange={setActivationStatus} options={[{ value: 1, label: '激活' }, { value: 2, label: '未激活' }, { value: 9, label: '未知' }]} style={{ width: '100%', height: 36 }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a' }}>上锁需求 <span style={{ color: '#ef4444' }}>*</span></div>
                                  <Select placeholder="请选择" value={lockStatus} onChange={setLockStatus} options={[{ value: 1, label: '上锁' }, { value: 2, label: '不上锁' }, { value: 9, label: '未知' }]} style={{ width: '100%', height: 36 }} />
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a' }}>揽收时间 <span style={{ color: '#ef4444' }}>*</span></div>
                                <Select
                                  placeholder="请选择"
                                  value={pickupMethod || undefined}
                                  onChange={setPickupMethod}
                                  options={[
                                    { value: '微签制作后5分钟内', label: '微签制作后5分钟内' },
                                    { value: '-', label: '未知' },
                                  ]}
                                  style={{ width: '100%', height: 36 }}
                                />
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a' }}>其他需求说明</div>
                                <TextArea placeholder="请输入其他需求说明，如颜色、成色等..." rows={2} value={requirementRemark} onChange={(e) => setRequirementRemark(e.target.value)} style={{ borderRadius: 6, fontSize: 13, padding: '8px 11px', resize: 'none' }} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 2. 预收 */}
            <div
              style={{
                border: `1px solid #e5e7eb`,
                borderRadius: 8,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
            >
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {renderRadioCircle(false)}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    <span style={{ fontWeight: 500, fontSize: 16, color: '#0a0a0a', lineHeight: '24px', fontFamily: 'sans-serif' }}>预收</span>
                    <span style={{ fontSize: 13, color: '#6b7280', lineHeight: '20px', fontFamily: 'sans-serif', marginTop: -2 }}>需要客户预付部分款项</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. 补充材料 */}
            <div
              style={{
                border: `1px solid #e5e7eb`,
                borderRadius: 8,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
            >
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {renderRadioCircle(false)}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                    <span style={{ fontWeight: 500, fontSize: 16, color: '#0a0a0a', lineHeight: '24px', fontFamily: 'sans-serif' }}>补充材料</span>
                    <span style={{ fontSize: 13, color: '#6b7280', lineHeight: '20px', fontFamily: 'sans-serif', marginTop: -2 }}>需要客户提供更多资料</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. 取消订单 */}
            <div
              style={{
                border: `1px solid ${activeTab === 'reject' ? '#2785f4' : '#e5e7eb'}`,
                borderRadius: 8,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                cursor: 'pointer',
              }}
              onClick={() => setActiveTab('reject')}
            >
              <div style={{ padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {renderRadioCircle(activeTab === 'reject')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 500, fontSize: 16, color: '#0a0a0a', lineHeight: '24px', fontFamily: 'sans-serif' }}>取消订单</span>
                  <span style={{ fontSize: 13, color: '#6b7280', lineHeight: '20px', fontFamily: 'sans-serif', marginTop: -2 }}>高风险或低利润，建议取消订单</span>
                </div>
              </div>
            </div>
          </div>

          <TextArea
            placeholder="请输入决策建议和决策依据..."
            value={decisionMemo}
            onChange={(e) => setDecisionMemo(e.target.value)}
            style={{ width: '100%', marginTop: 8, borderRadius: 8, fontSize: 14, padding: 16, borderColor: '#d1d5db', fontFamily: 'sans-serif', height: 120, resize: 'none' }}
          />
        </div>

        {/* Footer */}
        <div style={{ backgroundColor: '#f9fafb', borderTop: '1px solid rgba(0,0,0,0.1)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, gap: 12 }}>
          <Button onClick={handleCancelClick} style={{ height: 38, padding: '0 20px', borderRadius: 8, borderColor: '#e5e7eb', color: '#374151', fontWeight: 500, fontFamily: 'sans-serif', boxShadow: 'none' }}>
            取消
          </Button>

          {activeTab === 'pass' && purchaseType === 'platform' && (
            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ height: 38, padding: '0 20px', borderRadius: 8, backgroundColor: '#2785f4', borderColor: '#2785f4', fontWeight: 500, color: 'white', fontFamily: 'sans-serif', boxShadow: 'none' }}>
              去人人租平台发货
            </Button>
          )}

          {activeTab === 'pass' && purchaseType === 'self' && (
            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ height: 38, padding: '0 20px', borderRadius: 8, backgroundColor: '#2785f4', borderColor: '#2785f4', fontWeight: 500, color: 'white', fontFamily: 'sans-serif', boxShadow: 'none' }}>
              下发采购需求
            </Button>
          )}

          {activeTab === 'advance' && (
            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ height: 38, padding: '0 20px', borderRadius: 8, backgroundColor: '#2785f4', borderColor: '#2785f4', fontWeight: 500, color: 'white', fontFamily: 'sans-serif', boxShadow: 'none' }}>
              通知审核去预收
            </Button>
          )}

          {activeTab === 'supplement' && (
            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ height: 38, padding: '0 20px', borderRadius: 8, backgroundColor: '#2785f4', borderColor: '#2785f4', fontWeight: 500, color: 'white', fontFamily: 'sans-serif', boxShadow: 'none' }}>
              通知审核补充材料
            </Button>
          )}

          {activeTab === 'reject' && (
            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ height: 38, padding: '0 20px', borderRadius: 8, backgroundColor: '#2785f4', borderColor: '#2785f4', fontWeight: 500, color: 'white', fontFamily: 'sans-serif', boxShadow: 'none' }}>
              通知运营取消订单
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DecisionModal;
